import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Typography,
  Card,
  CardMedia,
  Box,
  IconButton,
  Container,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import RightPreview from "./RightPreview";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage as farImage } from "@fortawesome/free-regular-svg-icons";
import { faVideo as fasVideo, faHome as fasHome, faFilePdf as fasPdf } from "@fortawesome/free-solid-svg-icons";
import { getFolder } from "../../api/services";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import PdfView from "./PdfView";

const ITEMS_PER_PAGE = 8;

type File = {
  id: string;
  name: string;
  fileType: "image" | "video" | "document";
  url: string;
  size: number;
  createdAt: string; // Assuming files have timestamps for sorting
};

type Folder = {
  id: string;
  title: string;
  files: File[];
};

const SharableFolder: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [sortOrder, setSortOrder] = useState<"latest" | "oldest">("latest"); // Sorting state
  // const [hidename, setHidename] = useState(false);

  const navigate = useNavigate();

  const { data: folderData, isLoading, isError } = useQuery(["folderData", id], () => getFolder(id || ""), {
    enabled: !!id,
  });

  const folder = folderData?.data;


  // useEffect(() => {
  //   if( folderData?.data?._id === "676fd57a5f2e680edf8554a4" || folderData?.data?._id === "676fd56c5f2e680edf8554a1" || folderData?.data?._id === "676fd5545f2e680edf85549e" ){
  //     setHidename(true);
  //   }
  // }, [folderData]); 




  if (isLoading) {
    return (
      <Box sx={{ padding: 4, textAlign: "center" }}>
        <LinearProgress />
      </Box>
    );
  }

  if (isError || !folder) {
    return (
      <Box sx={{ padding: 4, textAlign: "center" }}>
        <Typography variant="h4" color="error">
          Folder Not Found
        </Typography>
      </Box>
    );
  }




  const handleSelectFile = (index: number) => {
    setSelectedFileIndex(index);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setSelectedFileIndex(null);
  };

  const handleSortChange = (event: any) => {
    setSortOrder(event.target.value);
  };

  const handleNextFile = () => {
    if (selectedFileIndex !== null && sortedFiles) {
      const nextIndex = (selectedFileIndex + 1) % sortedFiles.length;
      setSelectedFileIndex(nextIndex);
      // Scroll the selected item into view
      const element = document.getElementById(`file-item-${nextIndex}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handlePreviousFile = () => {
    if (selectedFileIndex !== null && sortedFiles) {
      const previousIndex = selectedFileIndex === 0 ? sortedFiles.length - 1 : selectedFileIndex - 1;
      setSelectedFileIndex(previousIndex);
      // Scroll the selected item into view
      const element = document.getElementById(`file-item-${previousIndex}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Sorting the files based on selected order
  const sortedFiles = [...folder.filesdata].sort((a, b) => {
    return sortOrder === "latest"
      ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      : new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  return (
    <Box sx={{ backgroundColor: "white", minHeight: "100vh" }}>
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", padding: 2 }}>
          <Box sx={{ flex: 1, paddingRight: isPreviewOpen ? 4 : 0, width: isPreviewOpen ? "50%" : "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
              <Breadcrumbs sx={{ color: 'black' }} separator="/">
                <IconButton size="small" sx={{ color: 'black' }} onClick={() => navigate("/folders")}>
                  <FontAwesomeIcon icon={fasHome} />
                </IconButton>
                <MuiLink sx={{ color: 'black' }} component={Link} to="/folders" underline="hover">
                  All Folders
                </MuiLink>
                <MuiLink sx={{ color: 'black' }} component={Link} to={`/folders/${folder.id}`} underline="hover">
                  {folder.name.length > 10 ? `${folder.name.substring(0, 10)}...` : folder.name}
                </MuiLink>
              </Breadcrumbs>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, color: 'black' }}>
                <Typography variant="subtitle1" sx={{ color: 'black' }}>{folder.files?.length} Files</Typography>

                {/* Sorting Dropdown */}
                {/* <FormControl size="small" sx={{ minWidth: 'auto' }}>
                  <Select
                        value={sortOrder}
                        onChange={handleSortChange}
                        displayEmpty
                        sx={{
                          backgroundColor: "secondary.main",
                          color: "#ffffff",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                          "&:hover": { backgroundColor: "#3a3a3a" },
                          height: "30px",
                          width : "auto"
                        }}
                      >
                        <MenuItem value="latest">Latest</MenuItem>
                        <MenuItem value="oldest">Oldest</MenuItem>
                      </Select>
                    </FormControl> */}

              </Box>
            </Box>

            <Typography variant="h3" gutterBottom>
              {folder.title}
            </Typography>

            <Grid container spacing={3}>
              {sortedFiles.slice(0, visibleCount).map((file: File, index: number) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={file.id} id={`file-item-${index}`}>
                  {/* <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} onClick={() => handleSelectFile(index)}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        border: selectedFileIndex === index ? "2px solid #0f63a5" : "1px solid #0b283e",
                        padding: "1px",
                        height: "100%",
                        borderRadius: "20px",
                        transition: "border 0.3s ease",
                        transform: selectedFileIndex === index ? 'scale(1.02)' : 'scale(1)',
                        '&:hover': {
                          borderColor: selectedFileIndex === index ? "#0f63a5" : "#0b283e",
                        }
                      }}
                    >
                      <Box sx={{ p: 3, backgroundColor: "#f7f7f7", }}>
                        {file.fileType === "image" && (
                          <CardMedia
                            component="img"
                            image={`https://dprstorage.b-cdn.net${file.url}`}
                            alt={file.name}
                            sx={{ height: 150, objectFit: "contain", display: "block", margin: "auto", borderRadius: "0px" }}
                          />
                        )}
                        {file.fileType === "video" && (
                          <video
                            src={`https://dprstorage.b-cdn.net${file.url}`}
                            style={{ width: "100%", height: "150px", objectFit: "contain", borderRadius: "0px" }}
                            muted
                            loop
                          />
                        )}
                        {file.fileType === "document" && (
                          <iframe
                            src={`https://dprstorage.b-cdn.net${file.url}`}
                            style={{ width: "100%", height: "150px", objectFit: "contain", borderRadius: "0px" }}
                            onClick={() => handleSelectFile(index)}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", p: 1, backgroundColor: "#0f63a5" }}>
                        <Box sx={{ p: 1, display: "flex", alignItems: "center" }}>
                          {file.fileType === "image" && <FontAwesomeIcon icon={farImage} style={{ color: "#ffffff" }} />}
                          {file.fileType === "video" && <FontAwesomeIcon icon={fasVideo} style={{ color: "#ffffff" }} />}
                          {file.fileType === "document" && <FontAwesomeIcon icon={fasPdf} style={{ color: "#ffffff" }}/>}
                        </Box>
                        <Box sx={{ ml: 1, flex: 1 }}>
                          <Typography variant="subtitle1" noWrap>
                            {file.name.length > 25 ? `${file.name.substring(0, 24)}...` : file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                          {
                            file.size > 1024 * 1024 ? (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {(file.size / 1024).toFixed(2)} KB
                              </Typography>
                            )
                          }   
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div> */}
                  <motion.div
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleSelectFile(index)}
                  >
                    <Card
                      sx={{
                        cursor: "pointer",
                        borderRadius: "24px",
                        overflow: "hidden",
                        height: "100%",
                        background: "linear-gradient(145deg, #ffffff, #f4f8fc)",
                        border: selectedFileIndex === index
                          ? "2px solid #0f63a5"
                          : "1px solid rgba(15, 99, 165, 0.2)",
                        boxShadow:
                          selectedFileIndex === index
                            ? "0 10px 30px rgba(15,99,165,0.4)"
                            : "0 6px 18px rgba(0,0,0,0.08)",
                        transition: "all 0.4s ease",
                        "&:hover": {
                          boxShadow: "0 15px 40px rgba(15,99,165,0.35)",
                        },
                      }}
                    >
                      {/* Preview Section */}
                      <Box
                        sx={{
                          p: 3,
                          background: "linear-gradient(135deg, #0f63a5 0%, #0b283e 100%)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: 170,
                        }}
                      >
                        {file.fileType === "image" && (
                          <CardMedia
                            component="img"
                            image={`https://dprstorage.b-cdn.net${file.url}`}
                            alt={file.name}
                            sx={{
                              maxHeight: 130,
                              objectFit: "contain",
                              borderRadius: "12px",
                              backgroundColor: "#ffffff",
                              padding: 1,
                            }}
                          />
                        )}

                        {file.fileType === "video" && (
                          <video
                            src={`https://dprstorage.b-cdn.net${file.url}`}
                            style={{
                              width: "100%",
                              maxHeight: "130px",
                              objectFit: "contain",
                              borderRadius: "12px",
                              background: "#fff",
                              padding: "5px",
                            }}
                            muted
                            loop
                          />
                        )}
                        {file.fileType === "document" && (
                          <Box
                            sx={{
                              width: "100%",
                              height: "140px",
                              backgroundColor: "#ffffff",
                              borderRadius: "16px",
                              overflow: "hidden",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >

                            <iframe
                              src={`https://dprstorage.b-cdn.net${file.url}`}
                              style={{
                                 width: "100%", height: "150px", objectFit: "contain", 
                                 borderRadius: "20px" 
                                }}
                              onClick={() => handleSelectFile(index)}
                            />
                          </Box>
                        )}

                        {/* {file.fileType === "document" && (
        <FontAwesomeIcon
          icon={fasPdf}
          style={{
            fontSize: "70px",
            color: "#ffffff",
          }}
        />
      )} */}
                      </Box>

                      {/* Bottom Info Section */}
                      <Box
                        sx={{
                          px: 2.5,
                          py: 2,
                          backgroundColor: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "12px",
                            backgroundColor: "#0f63a5",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            mr: 2,
                            boxShadow: "0 4px 12px rgba(15,99,165,0.3)",
                          }}
                        >
                          {file.fileType === "image" && (
                            <FontAwesomeIcon icon={farImage} style={{ color: "#fff" }} />
                          )}
                          {file.fileType === "video" && (
                            <FontAwesomeIcon icon={fasVideo} style={{ color: "#fff" }} />
                          )}
                          {file.fileType === "document" && (
                            <FontAwesomeIcon icon={fasPdf} style={{ color: "#fff" }} />
                          )}
                          {/* {file.fileType === "document" && (
  <iframe
    src={`https://dprstorage.b-cdn.net${file.url}`}
    style={{ width: "100%", height: "150px", objectFit: "contain", borderRadius: "0px" }}
    onClick={() => handleSelectFile(index)}
  />
)} */}

                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            noWrap
                            sx={{
                              fontWeight: 600,
                              color: "#0b283e",
                            }}
                          >
                            {file.name.length > 25
                              ? `${file.name.substring(0, 24)}...`
                              : file.name}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color: "#0f63a5",
                              fontWeight: 500,
                            }}
                          >
                            {file.size > 1024 * 1024
                              ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                              : `${(file.size / 1024).toFixed(2)} KB`}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>

                </Grid>
              ))}
            </Grid>

            {visibleCount < folder.files.length && (
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Button variant="contained" sx={{ background: '#0f63a5' }} onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_PAGE)}>
                  Show More
                </Button>
              </Box>
            )}
          </Box>
          {isPreviewOpen && selectedFileIndex !== null && sortedFiles[selectedFileIndex].fileType !== "document" && (
            <RightPreview
              file={sortedFiles[selectedFileIndex]}
              onClose={handleClosePreview}
              onNext={handleNextFile}
              onPrevious={handlePreviousFile}
            />
          )}
          {isPreviewOpen && selectedFileIndex !== null && sortedFiles[selectedFileIndex].fileType === "document" && (
            <PdfView
              fileUrl={`https://dprstorage.b-cdn.net${sortedFiles[selectedFileIndex].url}`}
              onClose={handleClosePreview}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default SharableFolder;
